import { useMutation, useQuery } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Spinner from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import { UPDATE_PRICE_LIST_ITEMS } from "pages/sales/priceLists/graphql";
import { useState } from "react";
import { BsArrowRight } from "react-icons/bs";
import { FETCH_DATA_FOR_SYNC_PRICE_BACK } from "./graphql";

const SyncPriceToPriceListView = ({ priceListId, lines }) => {
  const { loading, error, data } = useQuery(FETCH_DATA_FOR_SYNC_PRICE_BACK, {
    variables: { id: priceListId },
    fetchPolicy: "network-only",
  });

  const [selectedLineIds, setSelectedLineIds] = useState([]);

  const [updatePrices, updatePricesRes] = useMutation(UPDATE_PRICE_LIST_ITEMS, {
    onCompleted() {
      Alert("success", "Prices updated successfully");
      setSelectedLineIds([]);
    },
    onError(error) {
      window.alert(parseError(error));
    },
    refetchQueries: [FETCH_DATA_FOR_SYNC_PRICE_BACK],
    awaitRefetchQueries: true,
  });

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  if (!data || !data.priceList) {
    return (
      <div className="text-red-600 font-semibold">
        No price list found for ID: {priceListId}.
      </div>
    );
  }

  const computedLines = lines.map((line) => {
    const priceListItem = data.priceList.latestPrices.find(
      (x) => x.product.id === line.product.id,
    );

    return {
      product: line.product,
      priceListValue: priceListItem ? priceListItem.price : "Not Found",
      planValue: parseFloat(line.salesPrice),
    };
  });

  const linesWithDifferentPrices = computedLines.filter(
    (line) => line.priceListValue !== line.planValue,
  );
  const selectedLinesWithDifferentPrices = linesWithDifferentPrices.filter(
    (line) => selectedLineIds.includes(line.product.id),
  );

  function syncHandler() {
    updatePrices({
      variables: {
        priceListId,
        salesPrices: computedLines
          .filter((line) => selectedLineIds.includes(line.product.id))
          .map((line) => ({
            productId: line.product.id,
            price: line.planValue,
            discountPercentage: 0, // Assuming no discount for sync
            useFixedMargin: false, // Assuming no fixed margin for sync
            fixedMargin: 0, // Assuming no fixed margin for sync
          })),
      },
    });
  }

  return (
    <div className="text-sm">
      {linesWithDifferentPrices.length > 0 ? (
        <div>
          <p>
            Below shows the products in this order which has different price
            between the price list.{" "}
          </p>
          <p>
            Choose the ones you want to update back to the pricelist and click
            Sync.
          </p>
        </div>
      ) : (
        <div className="text-green-600 font-semibold">
          All products in this plan have the same price as the price list. No
          need to sync.
        </div>
      )}

      <div className="-mx-2 mt-4">
        <table>
          <thead className=" whitespace-nowrap">
            <tr>
              <th className="w-6">
                <input
                  type="checkbox"
                  checked={
                    selectedLineIds.length > 0 &&
                    selectedLineIds.length === linesWithDifferentPrices.length
                  }
                  onChange={() => {
                    // Toggle all lines with different prices
                    if (
                      selectedLineIds.length === linesWithDifferentPrices.length
                    ) {
                      setSelectedLineIds([]);
                    } else {
                      setSelectedLineIds(
                        linesWithDifferentPrices.map((line) => line.product.id),
                      );
                    }
                  }}
                />
              </th>
              <th className="w-full">Product</th>
              <th className="text-right">
                {data.priceList.name}({data.priceList.currency})
              </th>
              <th className="text-right">
                <BsArrowRight />
              </th>
              <th className="text-right">Plan Value</th>
            </tr>
          </thead>
          <tbody>
            {computedLines.map((line, index) => (
              <tr
                key={index}
                className={`border-y dark:border-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700
                  ${line.priceListValue === line.planValue ? " opacity-30 cursor-not-allowed" : "bg-green-100 hover:bg-green-200 dark:bg-green-800 dark:hover:bg-green-700"}
                  `}
                onClick={() => {
                  if (line.priceListValue === line.planValue) return;

                  setSelectedLineIds((prev) =>
                    prev.includes(line.product.id)
                      ? prev.filter((id) => id !== line.product.id)
                      : [...prev, line.product.id],
                  );
                }}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedLineIds.includes(line.product.id)}
                    readOnly
                  />
                </td>
                <td>
                  <div>
                    <div>{line.product.number}</div>
                    <div className="text-xs opacity-70">
                      {line.product.name}
                    </div>
                  </div>
                </td>
                <td className="text-right">{line.priceListValue}</td>
                <td className="text-right">
                  <BsArrowRight />
                </td>
                <td className="text-right">{line.planValue}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-2 text-xs opacity-70 italic">
        {selectedLineIds.length > 0
          ? `${selectedLineIds.length} products selected.`
          : `Choose at least one product to sync.`}
      </div>

      <div className="mt-6">
        <Button
          primary
          disabled={selectedLinesWithDifferentPrices.length === 0}
          onClick={syncHandler}
          loading={updatePricesRes.loading}
        >
          Sync
        </Button>
      </div>
    </div>
  );
};

export default SyncPriceToPriceListView;
